import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsOpeningInViewer } from '~/hooks/openInViewer';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import AppConfig from '~/utils/appconfig';

export const PATIENT_LIST_PATH = '/patients';

export type ReturnToPatientList = {
    /** Whether this deployment and route offer a way back at all. */
    available: boolean;
    /** True when returning would discard a loaded patient, so it must be confirmed. */
    closesPatient: boolean;
    returnToList: () => void;
};

/**
 * The way back from the viewer to the worklist, shared by the patient
 * breadcrumb menu and the plain header control shown when no patient is
 * loaded (a failed open leaves the viewer empty, which is exactly when the
 * user must be able to leave). Returning always closes the loaded patient,
 * so the worklist never shows alongside a patient it is not listing.
 */
export function useReturnToPatientList(): ReturnToPatientList {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const selectedPatient = useSelectedPatient();
    const { unloadCurrentPatient } = useUnloadPatient();
    const openingInViewer = useIsOpeningInViewer();

    const returnToList = useCallback((): void => {
        if (selectedPatient) {
            unloadCurrentPatient();
        }
        navigate(PATIENT_LIST_PATH);
    }, [selectedPatient, unloadCurrentPatient, navigate]);

    // The list only exists in connector deployments; it is not somewhere to
    // navigate to from itself; and a pre-established session was launched
    // from a TPS, where leaving would tear the session down with no way back.
    // Nor while an open is in progress: its parse is not interruptible, so
    // leaving would abandon the commit it is making.
    const available =
        AppConfig.isConnectorMode &&
        !openingInViewer &&
        pathname !== PATIENT_LIST_PATH &&
        !pathname.startsWith('/session/');

    return { available, closesPatient: Boolean(selectedPatient), returnToList };
}
